import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Download, PlayCircle, CheckCircle2, Factory, ClipboardList, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { key: "Approved", next: "plan_production", nextLabel: "Plan Production", icon: Factory },
  { key: "Production Planned", next: "start_production", nextLabel: "Start Production", icon: PlayCircle },
  { key: "In Production", next: "complete_production", nextLabel: "Mark Completed", icon: CheckCircle2 },
  { key: "Production Completed", next: null, nextLabel: "", icon: null },
];

export default function Production() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stageKey, setStageKey] = useState("Approved");
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRemarks, setBulkRemarks] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [intel, setIntel] = useState(null);

  const current = STAGES.find((s) => s.key === stageKey);

  useEffect(() => { api.get("/orders", { params: { status: stageKey } }).then((r) => setOrders(r.data)); setSelected(new Set()); }, [stageKey]);
  useEffect(() => { api.get("/production/intelligence").then((r) => setIntel(r.data)).catch(() => {}); }, [stageKey]);

  function toggle(id) { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); }
  function toggleAll() { setSelected(selected.size === orders.length ? new Set() : new Set(orders.map((o) => o.id))); }

  async function openPreview() {
    if (selected.size === 0) return toast.error("Select at least one order");
    setLoadingPreview(true); setPreviewOpen(true);
    try {
      const { data } = await api.post("/production/sheet/preview", { order_ids: Array.from(selected) });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setPreviewOpen(false); }
    finally { setLoadingPreview(false); }
  }

  async function downloadSheet() {
    if (selected.size === 0) return toast.error("Select at least one order");
    const token = localStorage.getItem("oms_token");
    try {
      const res = await fetch(`${API}/production/sheet/pdf`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ order_ids: Array.from(selected) }) });
      if (!res.ok) throw new Error(); const b = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "production_sheet.pdf"; a.click();
    } catch { toast.error("Failed to generate"); }
  }

  async function singleAction(id) {
    if (!current?.next) return;
    try {
      await api.post(`/orders/${id}/action`, { action: current.next, remarks: "" });
      toast.success(current.nextLabel); api.get("/orders", { params: { status: stageKey } }).then((r) => setOrders(r.data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function bulkRun() {
    if (!current?.next || selected.size === 0) return;
    try {
      const { data } = await api.post(`/orders/bulk-action`, { action: current.next, order_ids: Array.from(selected), remarks: bulkRemarks });
      const ok = data.results.filter((r) => r.ok).length; const fail = data.results.length - ok;
      toast.success(`${ok} succeeded${fail ? ` · ${fail} skipped` : ""}`);
      setBulkOpen(false); setBulkRemarks(""); setSelected(new Set());
      api.get("/orders", { params: { status: stageKey } }).then((r) => setOrders(r.data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  const canAct = ["super_admin", "admin", "production", "operations"].includes(user?.role);
  const Icon = current?.icon || Factory;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Production</h1>
          <p className="text-sm text-slate-500 mt-1">Stock-aware planning: net production = required − on-hand stock. Formulas determine batch counts.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openPreview} data-testid="prod-plan-btn"><ClipboardList className="h-4 w-4 mr-1.5" /> Plan Production ({selected.size})</Button>
          <Button variant="outline" onClick={downloadSheet} data-testid="prod-sheet-btn"><Download className="h-4 w-4 mr-1.5" /> Production Sheet PDF ({selected.size})</Button>
        </div>
      </div>

      <Tabs value={stageKey} onValueChange={setStageKey}>
        <TabsList className="bg-slate-100 p-1">
          {STAGES.map((s) => <TabsTrigger key={s.key} value={s.key} data-testid={`prod-tab-${s.key.toLowerCase().replace(/\s+/g, "-")}`}>{s.key}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {intel && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="prod-intel">
          {intel.stages.map((s) => (
            <Card key={s.stage} className={`p-3.5 border border-slate-200 shadow-none rounded-xl ${s.stage === stageKey ? "ring-2 ring-amber-500 ring-offset-1" : ""}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{s.stage}</div>
              <div className="text-[22px] font-bold text-slate-900 tabular-nums mt-0.5 leading-none">{s.order_count}</div>
              <div className="text-[11px] text-slate-500 mt-1">
                {s.total_cases > 0 ? <>{Number(s.total_cases).toLocaleString()} cases · {Number(s.total_weight_kg).toLocaleString()} kg</> : <span className="text-slate-400">no orders</span>}
              </div>
            </Card>
          ))}
          <Card className="p-3.5 border border-emerald-200 bg-emerald-50/40 shadow-none rounded-xl">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium">Approved Queue · Net Production</div>
            <div className="text-[22px] font-bold text-emerald-800 tabular-nums mt-0.5 leading-none">{Number(intel.approved_queue.total_net_required).toLocaleString()}</div>
            <div className="text-[11px] text-emerald-700 mt-1">{intel.approved_queue.distinct_skus} SKUs · stock cover {intel.approved_queue.stock_coverage_pct}%</div>
            {intel.approved_queue.skus_missing_formula_count > 0 && (
              <div className="text-[11px] text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {intel.approved_queue.skus_missing_formula_count} SKU(s) need formula</div>
            )}
          </Card>
        </div>
      )}

      {canAct && current?.next && selected.size > 0 && (
        <Card className="p-3 border border-slate-900 bg-slate-900 text-white rounded-xl flex items-center justify-between">
          <div className="text-sm"><b>{selected.size}</b> selected in <b>{stageKey}</b></div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800" onClick={() => setSelected(new Set())}>Clear</Button>
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => setBulkOpen(true)} data-testid="prod-bulk-next"><Icon className="h-4 w-4 mr-1.5" /> {current.nextLabel} ({selected.size})</Button>
          </div>
        </Card>
      )}

      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="p-3.5 w-8"><Checkbox checked={orders.length > 0 && selected.size === orders.length} onCheckedChange={toggleAll} data-testid="prod-select-all" /></th>
                <th className="p-3.5 text-left">PFI</th>
                <th className="p-3.5 text-left">Distributor</th>
                <th className="p-3.5 text-left">Region</th>
                <th className="p-3.5 text-left">Date</th>
                <th className="p-3.5 text-right">Cases</th>
                <th className="p-3.5 text-right">Weight (kg)</th>
                <th className="p-3.5 text-left">Plan Date</th>
                <th className="p-3.5 text-left">Status</th>
                <th className="p-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.length === 0 ? <tr><td colSpan={10} className="p-12 text-center text-slate-500">No orders in this stage.</td></tr> :
                orders.map((o) => (
                  <tr key={o.id} className="table-row-hover">
                    <td className="p-3.5"><Checkbox checked={selected.has(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`prod-sel-${o.pfi_number}`} /></td>
                    <td className="p-3.5 mono text-[12px] font-semibold">{o.pfi_number}</td>
                    <td className="p-3.5">{o.distributor_name}</td>
                    <td className="p-3.5 text-slate-600 text-xs">{o.region_name || o.region}</td>
                    <td className="p-3.5 text-slate-600">{fmtDate(o.order_date)}</td>
                    <td className="p-3.5 text-right font-medium">{o.total_dispatch_quantity || o.total_quantity}</td>
                    <td className="p-3.5 text-right">{Number(o.total_weight || 0).toFixed(1)}</td>
                    <td className="p-3.5 text-slate-600 text-xs">{fmtDate(o.production?.plan_date)}</td>
                    <td className="p-3.5"><StatusBadge status={o.status} /></td>
                    <td className="p-3.5 text-right">
                      <div className="flex justify-end gap-1.5">
                        <Link to={`/orders/${o.id}`} className="text-blue-600 hover:underline text-xs">Open</Link>
                        {canAct && current?.next && <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => singleAction(o.id)} data-testid={`prod-push-${o.pfi_number}`}>{current.nextLabel}</Button>}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk: {current?.nextLabel}</DialogTitle><DialogDescription className="text-sm text-slate-500 mt-1">Apply to {selected.size} selected orders.</DialogDescription></DialogHeader>
          <div><Label>Remarks</Label><Textarea value={bulkRemarks} onChange={(e) => setBulkRemarks(e.target.value)} rows={3} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkRun} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="prod-bulk-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading text-xl">Production Plan · Stock-Aware Preview</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Net production = required − on-hand FG stock. Batches derived from each SKU's active formula.</DialogDescription>
          </DialogHeader>
          {loadingPreview ? <div className="p-12 text-center text-slate-500">Calculating…</div> : preview ? (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-xs">
                {preview.orders.map((o) => <span key={o.id} className="px-2 py-1 bg-slate-100 rounded mono">{o.pfi_number}</span>)}
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">Date: {preview.date}</span>
              </div>
              {preview.totals && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5" data-testid="prod-preview-totals">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Distinct SKUs</div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{preview.totals.skus}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Required</div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{Number(preview.totals.total_required).toLocaleString()}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">On-Hand Stock</div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{Number(preview.totals.total_stock).toLocaleString()}</div>
                    <div className="text-[10px] text-slate-500">{preview.totals.stock_coverage_pct}% cover</div>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Net Production</div>
                    <div className="text-lg font-bold text-emerald-800 tabular-nums">{Number(preview.totals.total_net_required).toLocaleString()}</div>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700">Total Batches</div>
                    <div className="text-lg font-bold text-amber-800 tabular-nums">{preview.totals.total_batches}</div>
                    {preview.totals.skus_missing_formula > 0 && <div className="text-[10px] text-amber-700">⚠ {preview.totals.skus_missing_formula} SKU(s) missing formula</div>}
                  </div>
                </div>
              )}
              <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
                      <tr>
                        <th className="p-2.5 text-left">SKU</th>
                        <th className="p-2.5 text-left">Name</th>
                        <th className="p-2.5 text-left">Category</th>
                        <th className="p-2.5 text-right">Required</th>
                        <th className="p-2.5 text-right">FG Stock</th>
                        <th className="p-2.5 text-right">Net Required</th>
                        <th className="p-2.5 text-left">Formula</th>
                        <th className="p-2.5 text-right">Batch Qty</th>
                        <th className="p-2.5 text-right">Batches</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.rows.map((r, i) => {
                        const cover = r.current_stock >= r.required_qty;
                        const noFormula = !r.has_formula && r.net_required > 0;
                        return (
                          <tr key={i} className={cover ? "bg-emerald-50/40" : (noFormula ? "bg-amber-50/40" : "")}>
                            <td className="p-2.5 mono text-[12px] font-semibold">{r.sku}</td>
                            <td className="p-2.5 text-slate-900 max-w-[260px] truncate">{r.name}</td>
                            <td className="p-2.5 text-slate-600 text-xs">{r.category}</td>
                            <td className="p-2.5 text-right font-medium">{r.required_qty}</td>
                            <td className="p-2.5 text-right">{r.current_stock}</td>
                            <td className="p-2.5 text-right font-semibold">{r.net_required === 0 ? <span className="text-emerald-700">0</span> : <span className="text-slate-900">{r.net_required}</span>}</td>
                            <td className="p-2.5 text-xs">{r.formula_id ? <span className="mono">{r.formula_id}</span> : <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> None</span>}</td>
                            <td className="p-2.5 text-right text-slate-700">{r.batch_output_qty || "—"}</td>
                            <td className="p-2.5 text-right font-bold text-slate-900">{r.batches_required || (r.net_required === 0 ? <span className="text-emerald-700 font-normal">none</span> : <span className="text-amber-700 font-normal">—</span>)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
              <div className="flex gap-4 flex-wrap text-xs text-slate-600">
                <div><span className="inline-block w-3 h-3 rounded bg-emerald-200 mr-1.5 align-middle" /> Fully covered by stock</div>
                <div><span className="inline-block w-3 h-3 rounded bg-amber-200 mr-1.5 align-middle" /> Missing formula</div>
              </div>
              {preview.free_articles?.length > 0 && (
                <Card className="p-3 border border-emerald-200 bg-emerald-50/50 rounded-xl">
                  <div className="text-xs uppercase tracking-wide text-emerald-800 font-semibold mb-2">Free Articles to Procure</div>
                  <div className="flex flex-wrap gap-2">
                    {preview.free_articles.map((fa, i) => <span key={i} className="px-2 py-1 bg-white border border-emerald-200 rounded text-xs">{fa.name} × {fa.qty}</span>)}
                  </div>
                </Card>
              )}
            </div>
          ) : <div className="p-12 text-center text-slate-500">No data.</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
            <Button onClick={downloadSheet} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="preview-download-pdf"><Download className="h-4 w-4 mr-1.5" /> Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
